import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, formatBytes, formatDate, type ItemPapelera } from './api';
import { IconoDe } from './Icono';
import { Confirmar, MenuAcciones } from './ui';

const DIA_MS = 24 * 3600 * 1000;

function diasRestantes(venceEn: string): number {
  return Math.max(0, Math.ceil((new Date(venceEn).getTime() - Date.now()) / DIA_MS));
}

export function Papelera({
  onQuota,
}: {
  onQuota: (q: { usedBytes: string; totalBytes: string }) => void;
}) {
  const [items, setItems] = useState<ItemPapelera[]>([]);
  const [retencion, setRetencion] = useState(30);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [porEliminar, setPorEliminar] = useState<ItemPapelera | null>(null);
  const [vaciando, setVaciando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const r = await api.papelera();
      setItems(r.items);
      setRetencion(r.retencionDias);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cargar la papelera.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Restaurar o eliminar cambia lo ocupado: la barra de cuota se refresca.
  async function refrescarCuota() {
    try {
      onQuota((await api.list('')).quota);
    } catch {
      // Solo es la barra: si falla, se corrige en la próxima carga del explorador.
    }
  }

  async function ejecutar(accion: () => Promise<string | null>) {
    setError(null);
    setAviso(null);
    try {
      setAviso(await accion());
      await cargar();
      await refrescarCuota();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos completar la operación.');
    }
  }

  const restaurar = (item: ItemPapelera) =>
    ejecutar(async () => {
      const { path } = await api.restaurarPapelera(item.id);
      const original = `${item.ubicacion}${item.nombre}${item.esCarpeta ? '/' : ''}`;
      return path === original
        ? `"${item.nombre}" volvió a ${item.ubicacion || 'Mis archivos'}.`
        : `Ya había algo con ese nombre: se restauró como "${path.replace(/\/$/, '')}".`;
    });

  const eliminar = (item: ItemPapelera) => {
    setPorEliminar(null);
    return ejecutar(async () => {
      await api.eliminarDePapelera(item.id);
      return null;
    });
  };

  const vaciar = () => {
    setVaciando(false);
    return ejecutar(async () => {
      const { eliminados } = await api.vaciarPapelera();
      return `Se eliminaron ${eliminados} ${eliminados === 1 ? 'elemento' : 'elementos'}.`;
    });
  };

  if (cargando) return <p className="vacio">Cargando…</p>;

  const total = items.reduce((suma, i) => suma + BigInt(i.sizeBytes), 0n);

  return (
    <section>
      <div className="barra-herramientas">
        <p className="nota-papelera">
          Lo que borrás queda acá {retencion} días y después se elimina solo. Mientras
          tanto sigue ocupando espacio.
          {items.length > 0 && <> Ahora: {formatBytes(total.toString())}.</>}
        </p>
        {items.length > 0 && (
          <button className="secundario" onClick={() => setVaciando(true)}>
            Vaciar papelera
          </button>
        )}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {aviso && (
        <p className="aviso-ok" role="status">
          {aviso}
        </p>
      )}

      {items.length === 0 ? (
        <p className="vacio">La papelera está vacía.</p>
      ) : (
        <div className="tabla-scroll">
          <table className="tabla">
            <thead>
              <tr>
                <th>Nombre</th>
                <th className="col-fecha">Ubicación original</th>
                <th className="col-fecha">Eliminado</th>
                <th className="col-tamanio">Se borra en</th>
                <th className="col-tamanio">Tamaño</th>
                <th className="col-acciones" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const dias = diasRestantes(item.venceEn);
                return (
                  <tr key={item.id}>
                    <td>
                      <span className="nombre-archivo">
                        <IconoDe nombre={item.nombre} esCarpeta={item.esCarpeta} /> {item.nombre}
                      </span>
                    </td>
                    <td className="col-fecha">{item.ubicacion ? `/${item.ubicacion}` : 'Mis archivos'}</td>
                    <td className="col-fecha">{formatDate(item.eliminadoEn)}</td>
                    <td className="col-tamanio">
                      <span className={dias <= 3 ? 'chip inactivo' : 'chip'}>
                        {dias === 0 ? 'hoy' : dias === 1 ? '1 día' : `${dias} días`}
                      </span>
                    </td>
                    <td className="col-tamanio">
                      {formatBytes(item.sizeBytes)}
                      {item.esCarpeta && item.objetos > 1 && (
                        <span className="sub"> · {item.objetos} elementos</span>
                      )}
                    </td>
                    <td className="col-acciones">
                      <MenuAcciones
                        etiqueta={`Acciones de ${item.nombre}`}
                        opciones={[
                          { etiqueta: 'Restaurar', onSelect: () => void restaurar(item) },
                          {
                            etiqueta: 'Eliminar definitivamente',
                            onSelect: () => setPorEliminar(item),
                            peligro: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {porEliminar && (
        <Confirmar
          titulo="Eliminar definitivamente"
          mensaje={`"${porEliminar.nombre}" se borra para siempre${porEliminar.esCarpeta ? ', con todo su contenido y su historial de versiones' : ', con su historial de versiones'}. No se puede deshacer.`}
          etiquetaAceptar="Eliminar"
          peligro
          onCancelar={() => setPorEliminar(null)}
          onAceptar={() => eliminar(porEliminar)}
        />
      )}

      {vaciando && (
        <Confirmar
          titulo="Vaciar papelera"
          mensaje={`Se borran para siempre ${items.length} ${items.length === 1 ? 'elemento' : 'elementos'} (${formatBytes(total.toString())}). No se puede deshacer.`}
          etiquetaAceptar="Vaciar"
          peligro
          onCancelar={() => setVaciando(false)}
          onAceptar={vaciar}
        />
      )}
    </section>
  );
}
